import { useCallback } from 'react';

import { getPaymentErrorContent, useAfterPurchaseModalStore } from '@/entities/afterPurchase';
import { usePurchaseConfirmationModalStore } from '@/entities/purchaseConfirmation';
import { RemovedBoostersList } from '@/entities/purchaseConfirmation/ui/components/RemovedBoostersList';
import { useReliableUserRefresh } from '@/entities/user/currentUser/model/useReliableUserRefresh';

import { API } from '@/shared/api';
import { IBoostersInvoicePreviewRequest, IBoostersInvoicePreviewResponse } from '@/shared/api/billing/types';
import { BoosterType } from '@/shared/types/Billing';

import { AllBoosters, useBoosters } from './useBoosters';

type BoosterSelection = {
  [BoosterType.SRT]?: boolean;
  [BoosterType.TeamspaceSeat]?: boolean;
  [BoosterType.TranslationPrompting]?: boolean;
};

type UseBoosterConfirmationOptions = {
  seatsCount: number;
  selectedBoosters?: BoosterSelection;
  onCancel?: () => void;
};

type SelectedBoostersMap = Record<string, { isSelected: boolean; quantity: number }>;

const buildBoostersPayload = (
  allBoosters: AllBoosters | null,
  selectedBoosters: SelectedBoostersMap,
) => {
  if (!allBoosters) return [];

  return Object.entries(selectedBoosters)
    .filter(([_, data]) => data.isSelected)
    .map(([type, data]) => {
      const booster = allBoosters[type as BoosterType];
      const priceId = booster?.prices[0]?.id;

      if (!priceId) return null;

      return {
        price_id: priceId,
        quantity: data.quantity,
      };
    })
    .filter((item): item is { price_id: string; quantity: number } => item !== null);
};

export const useBoosterConfirmation = () => {
  const openConfirmation = usePurchaseConfirmationModalStore((state) => state.open);
  const { allBoosters, purchasedBoosters } = useBoosters();
  const { reliableRefresh } = useReliableUserRefresh();

  const { openError, openSuccess } = useAfterPurchaseModalStore();

  const openBoosterConfirmation = useCallback(
    ({ seatsCount, selectedBoosters, onCancel }: UseBoosterConfirmationOptions) => {
      const boostersMap: SelectedBoostersMap = {
        [BoosterType.SRT]: {
          isSelected: selectedBoosters?.[BoosterType.SRT] ?? !!purchasedBoosters?.[BoosterType.SRT],
          quantity: 1,
        },
        [BoosterType.TeamspaceSeat]: {
          isSelected:
            selectedBoosters?.[BoosterType.TeamspaceSeat] ?? !!purchasedBoosters?.[BoosterType.TeamspaceSeat],
          quantity: seatsCount,
        },
        [BoosterType.TranslationPrompting]: {
          isSelected:
            selectedBoosters?.[BoosterType.TranslationPrompting] ??
            !!purchasedBoosters?.[BoosterType.TranslationPrompting],
          quantity: 1,
        },
      };

      openConfirmation({
        promoCodeConfig: null,
        renderExtra: () => (
          <RemovedBoostersList
            allBoosters={allBoosters}
            purchasedBoosters={purchasedBoosters}
            selectedBoosters={boostersMap}
          />
        ),
        fetchPreview: () => {
          const payload = buildBoostersPayload(allBoosters, boostersMap);
          return API.getBoostersInvoicePreview({
            boosters: payload as IBoostersInvoicePreviewRequest['boosters'],
          });
        },
        onCancel,
        onConfirm: async (invoice: IBoostersInvoicePreviewResponse) => {
          const payload = buildBoostersPayload(allBoosters, boostersMap);

          try {
            const res = await API.purchaseBoosters({
              boosters: payload as IBoostersInvoicePreviewRequest['boosters'],
            });

            if (!res.paid) {
              const errorContent = getPaymentErrorContent(res.error_code);
              openError(errorContent.message, res.invoice_url, errorContent.buttonText);
              return;
            }

            if (res.paid) {
              await reliableRefresh();

              const amountDueToday = invoice?.instant_payment?.amount ?? 0;
              const description =
                amountDueToday === 0
                  ? 'Your booster changes have been saved'
                  : 'Payment processed successfully, subscription changed';
              openSuccess(description, !!amountDueToday);
            }
          } catch (error) {
            console.error('Failed to purchase boosters:', error);
            const errorContent = getPaymentErrorContent();
            openError(errorContent.message, '', errorContent.buttonText);
            throw error;
          }
        },
      });
    },
    [allBoosters, purchasedBoosters, openConfirmation, reliableRefresh, openError, openSuccess],

  );

  return { openBoosterConfirmation };
};
